/**
 * A CLI artifact wearing the user's face.
 *
 *   owner, 2026-08-07: Claude Clean view 看起來有些問題. 使用者的輸入都變成 html
 *   標籤了.
 *
 * Claude Code records its own machinery as messages with role `user` — an interrupted
 * turn, a slash command, a background task finishing, the output of a `!` shell run.
 * The transcript is truthful about what happened and wrong about who said it, and Clean
 * View renders any unframed user block as the owner's own words. So a task-notification
 * appeared under their name with a YOU badge, angle brackets and all.
 *
 * THE TAGS BECAME VISIBLE BECAUSE OF MY LAST FIX, and that is worth being straight
 * about. Before it, marked passed these through as raw HTML and DOMPurify deleted every
 * unknown element while keeping its children, so the reader saw a run of ids and paths
 * with the tags silently stripped: still not the owner's words, still misattributed,
 * just quieter about it. Escaping made an existing bug legible rather than causing one.
 * The owner read the tags as evidence that something injects markup before parsing —
 * nothing does any more (Markdown.tsx builds the ping chip in a marked extension, at
 * render time) — but the report was right that this does not belong in a message body.
 *
 * COUNTED, NOT GUESSED. Across the 39 transcripts on this machine, of 2360 user text
 * blocks, 227 are machinery:
 *
 *   165  [bracket] forms, of which the interruptions are the family below
 *    67  <local-command-caveat>   boilerplate telling the MODEL to ignore what follows
 *    67  <command-name>           a slash command the owner typed
 *    65  <local-command-stdout>   what that command printed
 *    23  <task-notification>      a background task finished
 *     2  <bash-input> / <bash-stdout>
 *     1  <system-reminder>
 *
 * NOT HIDDEN, rendered as what they are. A dropped interruption leaves a turn that
 * stops for no visible reason, and a dropped task-notification leaves an agent replying
 * to nothing. Misattribution is the bug; the events are worth keeping.
 */

export type SystemNote = {
  /** What happened, in the reader's language. */
  label: string
  /** The part a person might actually want, or null when the block is pure boilerplate. */
  detail: string | null
}

const INTERRUPTED = /^\[Request interrupted by user[^\]]*\]$/

/**
 * The tag a harness block OPENS with, anchored at the start.
 *
 * Anchored on purpose, and whole-string where the shape allows. A message that merely
 * CONTAINS one of these — the owner quoting it, or an agent discussing this very bug,
 * which is a thing that happens in these transcripts — is a real message and has to stay
 * attributed to whoever sent it.
 */
const OPENS = /^<([a-z][a-z0-9-]*)>/

/** Inner text of the first `<tag>…</tag>`, unescaped enough to read. */
function inner(text: string, tag: string): string | null {
  const m = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`).exec(text)
  const v = m?.[1]?.trim()
  return v ? v : null
}

/*
 * ANSI escapes reach here. `local-command-stdout` carries real terminal output —
 * "Set model to \x1b[1mOpus 5\x1b[22m" — and rendering it raw prints the control bytes
 * as mojibake in the middle of a sentence. Stripped rather than interpreted: this is a
 * one-line notice, not a terminal.
 */
// eslint-disable-next-line no-control-regex
const ANSI = /\x1b\[[0-9;]*[A-Za-z]/g
const clean = (s: string | null): string | null => {
  const v = s?.replace(ANSI, '').trim()
  return v ? v : null
}

/** One line, so a notice cannot become a wall. The full text stays in Terminal view. */
function firstLine(s: string | null, max = 160): string | null {
  if (!s) return null
  const line = s.split('\n').find((l) => l.trim()) ?? ''
  const t = line.trim()
  return t ? (t.length > max ? `${t.slice(0, max - 1)}…` : t) : null
}

/**
 * The note to render instead of a user message, or null if this really is one.
 */
export function systemNote(text: string | null | undefined): SystemNote | null {
  const t = (text ?? '').trim()
  if (!t) return null

  if (INTERRUPTED.test(t)) {
    return {
      label: t.includes('for tool use') ? 'Interrupted before a tool ran' : 'Interrupted',
      detail: null,
    }
  }

  const tag = OPENS.exec(t)?.[1]
  if (!tag) return null

  switch (tag) {
    case 'task-notification':
      // The summary is the only human-facing field; the ids and the output path are
      // for the agent. Falls back to the status when a notification has no summary.
      return {
        label: 'Background task finished',
        detail: firstLine(clean(inner(t, 'summary') ?? inner(t, 'status'))),
      }
    case 'command-name':
      // `<command-name>/model</command-name>` — the command IS the interesting part,
      // so it becomes the label rather than a detail nobody would expand.
      return { label: `Ran ${clean(inner(t, 'command-name')) ?? 'a command'}`, detail: null }
    case 'local-command-stdout':
      return { label: 'Command output', detail: firstLine(clean(inner(t, tag))) }
    case 'local-command-caveat':
      /*
       * Boilerplate addressed to the MODEL — "DO NOT respond to these messages". It
       * accompanies every slash command and says nothing to a reader, so it carries no
       * detail; the `Ran /x` note beside it is the event.
       */
      return { label: 'Local command', detail: null }
    case 'bash-input':
      return { label: 'Ran in the shell', detail: firstLine(clean(inner(t, tag))) }
    case 'bash-stdout':
      return { label: 'Shell output', detail: firstLine(clean(inner(t, tag) ?? t.slice(tag.length + 2))) }
    case 'system-reminder':
      return { label: 'System reminder', detail: firstLine(clean(inner(t, tag))) }
    default:
      /*
       * An unrecognised tag is NOT machinery by default.
       *
       * The list above is what this machine's transcripts actually contain, and a new
       * harness block will not be on it. Guessing from the shape — "starts with a tag,
       * therefore not a person" — would silently restyle a real message that opens with
       * `<summary>` or `<note>`, and the reader would have no way to tell. Unknown text
       * stays the owner's, which is the failure that shows rather than hides.
       */
      return null
  }
}
